import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogBody, DialogFooter, Tag, Intent, Callout } from '@blueprintjs/core';
import { Suggest, ItemRenderer } from '@blueprintjs/select';
import { githubApi } from '../../../api/github';
import { useAppStore } from '../../../stores/UnifiedStoreProvider';

interface Repository {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description: string | null;
  owner_login: string;
}

interface LinkedRepository {
  id: number;
  name: string;
  full_name: string;
  html_url: string;
  description?: string;
}

interface RepositoryLinkerProps {
  nodeId: string;
  linkedRepositories: LinkedRepository[];
  onRepositoriesChange: (repositories: LinkedRepository[]) => void;
  mindmapId?: string;
  autoOpenDialog?: boolean; // New prop to auto-open the dialog
}

const RepositoryLinker: React.FC<RepositoryLinkerProps> = ({
  nodeId,
  linkedRepositories,
  onRepositoriesChange,
  mindmapId,
  autoOpenDialog = false
}) => {
  const appStore = useAppStore();
  const [isDialogOpen, setIsDialogOpen] = useState(autoOpenDialog);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRepo, setSelectedRepo] = useState<Repository | null>(null);

  console.log('[RepositoryLinker] Rendered for node:', nodeId, 'with repos:', linkedRepositories);

  // Load repositories when dialog opens
  useEffect(() => {
    if (isDialogOpen && repositories.length === 0) {
      loadRepositories();
    }
  }, [isDialogOpen]);

  const loadRepositories = async () => {
    console.log('[RepositoryLinker] Loading repositories...');
    setLoading(true);
    setError(null);
    try {
      // Check GitHub integration status first
      const healthCheck = await githubApi.health();
      console.log('[RepositoryLinker] GitHub health check:', healthCheck);
      
      if (!healthCheck.authenticated) {
        setError('GitHub not connected. Please connect your GitHub account in Settings.');
        setLoading(false);
        return;
      }
      
      // Check if we have cached repositories in the store
      const cachedRepos = appStore.mindmap.getCachedRepositories();
      if (cachedRepos && cachedRepos.length > 0) {
        console.log('[RepositoryLinker] Using cached repositories from store:', cachedRepos.length);
        setRepositories(cachedRepos);
        return;
      }
      
      // Use search with empty query to get all accessible repos (now includes org repos)
      const searchResult = await githubApi.searchRepositories('');
      console.log('[RepositoryLinker] Search result:', searchResult);
      
      // Safely extract items from searchResult, handling both object and array responses
      let repos = [];
      if (Array.isArray(searchResult)) {
        repos = searchResult;
      } else if (searchResult && typeof searchResult === 'object' && Array.isArray(searchResult.items)) {
        repos = searchResult.items;
      } else if (searchResult && typeof searchResult === 'object' && searchResult.total === 0) {
        repos = [];
      }
      
      console.log('[RepositoryLinker] Loaded repositories:', repos.length);
      
      // Ensure repos is an array before mapping
      if (!Array.isArray(repos)) {
        console.error('[RepositoryLinker] repos is not an array:', repos);
        setError('Failed to load repositories: Invalid response format');
        setLoading(false);
        return;
      }
      
      // Map to expected format
      const formattedRepos = repos.map((r: any) => ({
        id: r.id,
        name: r.name,
        full_name: r.fullName || r.full_name,
        private: r.isPrivate !== undefined ? r.isPrivate : r.private,
        html_url: r.url || r.html_url,
        description: r.description,
        owner_login: r.owner,
      }));
      
      // Cache in store for future opens
      appStore.mindmap.setCachedRepositories(formattedRepos);
      
      setRepositories(formattedRepos);
      
      if (formattedRepos.length === 0) {
        setError('No repositories found. Make sure you have access to repositories in your GitHub account or organization.');
      }
    } catch (err: any) {
      const errorMessage = `Failed to load repositories: ${err.message || 'Unknown error'}`;
      console.error('[RepositoryLinker] Error loading repositories:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRepository = async () => {
    if (!selectedRepo) return;

    // Check if already linked
    if (linkedRepositories.some(repo => repo.id === selectedRepo.id)) {
      setError('This repository is already linked to this node.');
      return;
    }

    const newLinkedRepo: LinkedRepository = {
      id: selectedRepo.id,
      name: selectedRepo.name,
      full_name: selectedRepo.full_name,
      html_url: selectedRepo.html_url,
      description: selectedRepo.description || undefined
    };

    const updatedRepos = [...linkedRepositories, newLinkedRepo];
    
    try {
      // Link via new backend API
      if (mindmapId) {
        await githubApi.linkRepoToNode(nodeId, mindmapId, {
          name: selectedRepo.name,
          owner: selectedRepo.owner_login,
          fullName: selectedRepo.full_name,
          full_name: selectedRepo.full_name,
          url: selectedRepo.html_url,
          html_url: selectedRepo.html_url,
          description: selectedRepo.description,
          isPrivate: selectedRepo.private,
          private: selectedRepo.private,
        });
      }
      
      // Update local state
      onRepositoriesChange(updatedRepos);
      setSelectedRepo(null);
      setIsDialogOpen(false);
    } catch (err: any) {
      setError(`Failed to link repository: ${err.message || 'Unknown error'}`);
      console.error('Error linking repository:', err);
    }
  };

  const handleRemoveRepository = async (repoId: number) => {
    const updatedRepos = linkedRepositories.filter(repo => repo.id !== repoId);
    
    try {
      // Update in database
      await db.transact(
        db.tx.mindmapnodes[nodeId].update({
          linkedRepositories: updatedRepos
        })
      );
      
      // Update local state
      onRepositoriesChange(updatedRepos);
    } catch (err: any) {
      console.error('Error unlinking repository:', err);
    }
  };

  const handleRepositoryClick = (repoUrl: string) => {
    window.open(repoUrl, '_blank', 'noopener,noreferrer');
  };

  // Filter out already linked repositories
  const availableRepositories = repositories.filter(
    repo => !linkedRepositories.some(linked => linked.id === repo.id)
  );

  const renderRepository: ItemRenderer<Repository> = (repo, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <div
        key={repo.id}
        onClick={handleClick}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: modifiers.active ? '#f5f8fa' : 'transparent'
        }}
      >
        <div style={{ fontWeight: 'bold', color: modifiers.active ? '#1F2937' : '#1F2937' }}>{repo.full_name}</div>
        {repo.description && (
          <div style={{ fontSize: '12px', color: '#5c7080', marginTop: '2px' }}>
            {repo.description}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#8a9ba8', marginTop: '2px' }}>
          {repo.private ? '🔒 Private' : '🌐 Public'}
        </div>
      </div>
    );
  };

  const filterRepository = (query: string, repo: Repository) => {
    const lowerQuery = query.toLowerCase();
    return (
      repo.name.toLowerCase().includes(lowerQuery) ||
      repo.full_name.toLowerCase().includes(lowerQuery) ||
      (repo.description && repo.description.toLowerCase().includes(lowerQuery))
    );
  };

  return (
    <>
      {/* Repository tags and add button */}
      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        {linkedRepositories.map(repo => (
          <Tag
            key={repo.id}
            intent={Intent.PRIMARY}
            round
            minimal
            onRemove={() => handleRemoveRepository(repo.id)}
            onClick={() => handleRepositoryClick(repo.html_url)}
            style={{ 
              cursor: 'pointer',
              fontSize: '11px',
              padding: '2px 8px',
              margin: '2px'
            }}
            title={`Click to open ${repo.full_name} | Right-click to remove`}
          >
            📁 {repo.name}
          </Tag>
        ))}
        <Button
          icon="plus"
          minimal
          small
          onClick={() => setIsDialogOpen(true)}
          title="Link a repository to this node"
          style={{ 
            minWidth: 'auto',
            padding: '2px 6px',
            fontSize: '11px'
          }}
        >
          Repo
        </Button>
        
        {/* Debug: Test GitHub connection */}
        <Button
          icon="refresh"
          minimal
          small
          onClick={async () => {
            try {
              console.log('[RepositoryLinker] Testing GitHub connection...');
              const connectionStatus = await githubApi.isConnected();
              console.log('[RepositoryLinker] GitHub connection status:', connectionStatus);
              alert(`GitHub connected: ${connectionStatus.connected}\nInstallation ID: ${connectionStatus.installationId}`);
            } catch (err) {
              console.error('[RepositoryLinker] GitHub connection test failed:', err);
              alert(`GitHub connection test failed: ${err}`);
            }
          }}
          title="Test GitHub connection"
          style={{ 
            minWidth: 'auto',
            padding: '2px 6px',
            fontSize: '11px'
          }}
        >
          Test
        </Button>
      </div>

      {/* Repository selection dialog */}
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Link Repository"
        style={{ width: '500px' }}
      >
        <DialogBody>
          {error && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: '15px' }}>
              {error}
            </Callout>
          )}
          
          <p style={{ marginBottom: '15px' }}>
            Select a repository to link to this node. Linked repositories will appear as clickable tags.
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading repositories...
            </div>
          ) : availableRepositories.length === 0 ? (
            <Callout intent={Intent.WARNING}>
              {repositories.length === 0 
                ? 'No repositories found. Make sure GitHub is connected and you have access to repositories.'
                : 'All available repositories are already linked to this node.'
              }
            </Callout>
          ) : (
            <Suggest<Repository>
              items={availableRepositories}
              itemRenderer={renderRepository}
              itemPredicate={filterRepository}
              onItemSelect={(repo) => setSelectedRepo(repo)}
              inputValueRenderer={(repo) => repo.full_name}
              popoverProps={{ minimal: true }}
              fill
              inputProps={{
                placeholder: 'Search repositories...',
                leftIcon: 'search'
              }}
              selectedItem={selectedRepo}
              resetOnSelect={false}
            />
          )}

          {selectedRepo && (
            <div style={{ marginTop: '15px', padding: '10px', border: '1px solid #d9e1e8', borderRadius: '3px' }}>
              <strong>Selected:</strong> {selectedRepo.full_name}
              {selectedRepo.description && (
                <div style={{ marginTop: '5px', fontSize: '13px', color: '#5c7080' }}>
                  {selectedRepo.description}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        
        <DialogFooter
          actions={
            <>
              <Button onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                intent={Intent.PRIMARY}
                onClick={handleAddRepository}
                disabled={!selectedRepo || loading}
              >
                Link Repository
              </Button>
            </>
          }
        />
      </Dialog>
    </>
  );
};

export default RepositoryLinker; 